import React, { useContext, useState } from "react";
import Ctx from "../../contexts/AppContext";
import { crd, bdg } from "../../constants/styles";
import Avatar from "../common/Avatar";
import { getLeaseForTenant, getLeaseStatus, leaseStatusColor, leaseStatusLabel } from "../../utils/leaseHelpers";
import { formatPropAddr } from "../../utils/propertyAddr";

const PAGE_SIZE = 4;

function TenantsPage() {
  // Delete / Payment History / WhatsApp invite all moved to
  // TenantDetailPage so their setters/helpers aren't needed here.
  const { t, h, tens, props, go, leases } = useContext(Ctx);

  // Show the first PAGE_SIZE tenants, then a "Show more" button reveals
  // the rest in place (no separate navigation needed — this IS the
  // tenants page).
  const [showAll, setShowAll] = useState(false);
  const visibleTens = showAll ? tens : tens.slice(0, PAGE_SIZE);
  const hiddenCount = tens.length - visibleTens.length;

  return <div style={{ paddingTop: 16 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.tenants}</h2>
      <button data-coach="tens-invite" onClick={() => go("addTenant")} style={{ background: h.ac, border: "none", color: "#fff", padding: "8px 16px", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>+ Add Tenant</button></div>
    {tens.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: 30, color: h.sc }}>{t.noTenFound}</div>}
    <div data-coach="tens-list">
    {visibleTens.map(te => {
      const p = props.find(x => x.tid === te.id);
      const tLease = getLeaseForTenant(leases, te.id);
      const tLeaseStatus = tLease ? getLeaseStatus(tLease) : null;
      return <div key={te.id} style={crd(h)}>
        {/* Header row: tapping the avatar / name / phone block opens the
            read-only tenant detail page (TenantDetailPage). The lease
            status badge is just a visual indicator and propagates the
            click upward, which is fine \u2014 landing on detail is the
            right outcome from anywhere on this row. */}
        <div
          onClick={() => { localStorage.setItem("editTid", te.id); go("tenantDetail"); }}
          style={{
            // Persistent accent tint signals tappability without
            // relying on hover (mobile-first). Header sits at the
            // top of the card, so we extend edge-to-edge with
            // rounded top corners — mirrors the bottom-of-card
            // pattern used on PropsListPage.
            background: `${h.ac}0E`,
            borderBottom: `1px solid ${h.ac}25`,
            display: "flex", alignItems: "center", gap: 10,
            cursor: "pointer",
            transition: "background 0.15s",
            margin: "-16px -16px 12px",
            padding: "14px 16px",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = `${h.ac}1A`; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = `${h.ac}0E`; }}
          title="View tenant details"
        >
          <Avatar src={te.avatar_url} name={te.name} size={36} bg={h.ac} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{te.name}</span>
              {te.verified ? <span title="Verified" style={{ color: h.gn, fontSize: 14 }}>{"\u2713"}</span> : null}
            </div>
            <div style={{ color: h.sc, fontSize: 11 }}>{te.ph}{te.id_type && te.id_number ? ` \u00B7 ${te.id_type.toUpperCase()}` : ""}</div>
          </div>
          {tLeaseStatus ? <span style={{ ...bdg(`${leaseStatusColor(tLeaseStatus, h)}20`, leaseStatusColor(tLeaseStatus, h)) }}>{leaseStatusLabel(tLeaseStatus, t)}</span>
            : <span style={{ ...bdg(`${h.sc}15`, h.sc) }}>{t.noLease}</span>}
          <span style={{ color: h.ac, fontSize: 16, fontWeight: 700, marginLeft: 2 }}>{"\u203A"}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: h.tx, marginBottom: 10, paddingBottom: 10, borderBottom: `1px dashed ${h.bd}` }}>
          <span>{t.property}: <b>{p ? formatPropAddr(p).split(",").slice(0, 2).join(",") : t.none}</b></span>
          <span>{t.joined}: {te.join}</span>
        </div>
        {/* View Payment History, Invite via WhatsApp and Delete moved
            to TenantDetailPage so the canonical actions all live in
            one place. Edit stays inline for quick access from the list. */}
        <button onClick={() => { localStorage.setItem("editTid", te.id); go("editTenant"); }}
          style={{ width: "100%", padding: 10, borderRadius: 10, border: "none", background: `${h.ac}15`, color: h.ac, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          {"✏️ " + t.edit}
        </button>
      </div>;
    })}
    </div>

    {/* Pagination — show first 4, then expand in place. */}
    {tens.length > PAGE_SIZE && (
      <button
        onClick={() => setShowAll(s => !s)}
        style={{
          width: "100%", marginTop: 4, marginBottom: 8,
          background: `${h.ac}10`, color: h.ac,
          border: `1px solid ${h.ac}30`,
          padding: "12px 14px", borderRadius: 10,
          cursor: "pointer", fontSize: 13, fontWeight: 600,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}
      >
        {showAll
          ? <>Show less <span style={{ fontSize: 13 }}>{"▲"}</span></>
          : <>Show more <span style={{ color: h.ts || h.sc, fontWeight: 500 }}>({hiddenCount} more)</span> <span style={{ fontSize: 13 }}>{"▼"}</span></>}
      </button>
    )}
  </div>;
}

export default TenantsPage;
